import React, { useEffect, useState } from "react";
import { Modal, Spin, Alert } from "antd";
import Cookies from "js-cookie";
import ProfileViewer from "../pages/borrower-dashboard/ProfileViewer";
import usePropertyApplicationCommentRefresh from "../hooks/usePropertyApplicationCommentRefresh";
import { getPersonData } from "../utils/myinfoData";
import { parseCbsReportFromPdf } from "../utils/parseCbsReportFromPdf";
import {
  getLoanProfileTabs,
  isTruthyFlag,
  shouldHideProfileContactInfo,
  SCCB_EXCLUDE_COLUMNS,
} from "../utils/loanProfile";

const API = "https://www.smart-lend.com/api";
const parsePayload = (value) => {
  if (!value || typeof value === "object") return value || null;
  try { return JSON.parse(value); } catch { return null; }
};

// `loan` is the original application, never the offer (whose user_id is the lender).
export default function LenderBorrowerProfileModal({ loanType, loan, borrowerName, isMobile, onClose }) {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [viewingUser, setViewingUser] = useState(0);
  const [viewingTab, setViewingTab] = useState(0);
  const [liveUserData, setLiveUserData] = useState(null);
  const [applicationComments, setApplicationComments] = useState([]);
  const token = Cookies.get("token");
  const isCorporate = loanType === "corporate";
  const isProperty = loanType === "property";
  const loanId = isCorporate ? loan?.loan_id : loan?.id;
  const userId = loan?.borrower_id || loan?.user_id || loan?.borrower?.user_id || loan?.borrower_data?.user_id;

  usePropertyApplicationCommentRefresh(
    isProperty ? loanId : null, true, token, setApplicationComments,
  );

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    setProfile(null);
    setError("");
    setViewingTab(0);
    setViewingUser(0);
    setLiveUserData(null);
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    const load = async () => {
      if (!userId) throw new Error("The borrower profile is unavailable for this application.");
      if (loanId && token) {
        fetch(`${API}/loan/marketplace-audit`, {
          method: "POST", headers,
          body: JSON.stringify({ loan_type: loanType, loan_id: loanId, action: "viewed" }),
        }).catch((error) => console.error("Unable to record marketplace audit:", error));
      }
      const response = await fetch(`${API}/user/getBorrowerSingppass?user_id=${encodeURIComponent(userId)}`, {
        headers, signal: controller.signal,
      });
      if (!response.ok) throw new Error("Unable to load the borrower profile. Please try again.");
      const identity = await response.json();
      const singpassData = parsePayload(identity.singpass_data);
      const myinfoData = parsePayload(identity.myinfo_data);
      let otherAuthoriserDetails = null;
      const combinedSingpassData = singpassData ? [singpassData] : [];
      if (isCorporate && loan.authorization_required) {
        const authorisersResponse = await fetch(`${API}/loanDetails/getLoanAuthorisers`, {
          method: "POST", headers, signal: controller.signal,
          body: JSON.stringify({ loan_id: loanId }),
        });
        if (!authorisersResponse.ok) throw new Error("Unable to load the loan authorisers. Please try again.");
        const body = await authorisersResponse.json();
        otherAuthoriserDetails = body.authorisers || [];
        combinedSingpassData.push(...otherAuthoriserDetails.map((person) => parsePayload(person.singpass_data)).filter(Boolean));
      }
      const loanData = isProperty ? parsePayload(loan.loan_data) || {} : loan;
      let allCBSData = [];
      if (isCorporate) {
        allCBSData = [
          { name: getPersonData(loan.singpass_data)?.name?.value || getPersonData(singpassData)?.name?.value, cbs_data: parsePayload(loan.credit_report_data) },
          ...(loan.loan_authorizations || []).map((person) => ({ name: person.director_name, cbs_data: parsePayload(person.credit_report_data) })),
        ];
      } else {
        const extracted = loanData.cbs_report_document?.extracted_data;
        let cbsData = extracted ? {
          RiskGrade: extracted.risk_grade,
          CreditScore: extracted.credit_score,
          ProbabilityofDefault: extracted.probability_default,
          SecuredCreditLimit: extracted.secured_credit_limit,
          UnsecuredCreditLimit: extracted.unsecured_credit_limit,
        } : null;
        if (loanData.cbs_report_document?.file_url) {
          try {
            const parsed = await parseCbsReportFromPdf(loanData.cbs_report_document.file_url);
            if (parsed) cbsData = { ...(cbsData || {}), ...parsed };
          } catch (error) { console.error("Unable to parse CBS report:", error); }
        }
        allCBSData = cbsData ? [{ user_id: userId, name: borrowerName, cbs_data: cbsData }] : [];
      }
      if (disposed) return;
      setLiveUserData(singpassData);
      setProfile({
        singpassData, myinfoData, otherAuthoriserDetails, combinedSingpassData, allCBSData,
        hideContactInfo: shouldHideProfileContactInfo(loan) || isTruthyFlag(identity.sl_created),
        sccbReportData: isProperty ? (
          loan.sccb_report?.response_payload ||
          (loan.sccb_reports || loan.sccbReports || []).find((report) => report.response_payload)?.response_payload || null
        ) : null,
      });
    };
    load().catch((error) => { if (!disposed) setError(error.message); });
    return () => { disposed = true; controller.abort(); };
  }, [loan, loanType, loanId, userId, token, isCorporate, isProperty, borrowerName]);

  return (
    <Modal className="sl-loan-action-modal" width={isMobile ? "100%" : "60%"}
      style={{ backgroundColor: "rgba(0, 0, 0, 0)" }} open onCancel={onClose} footer={null}>
      {error ? <Alert type="error" message={error} /> : !profile ? <Spin /> :
        profile.singpassData || profile.myinfoData || isProperty ? (
          <ProfileViewer
            keyman={isCorporate ? loan.keyman : ""}
            isMobile={isMobile}
            viewingUser={viewingUser} setViewingUser={setViewingUser}
            viewingTab={viewingTab} setViewingTab={setViewingTab}
            liveUserData={liveUserData} setLiveUserData={setLiveUserData}
            singpassData={profile.singpassData} myinfoData={profile.myinfoData}
            otherAuthoriserDetails={profile.otherAuthoriserDetails}
            combinedSingpassData={profile.combinedSingpassData}
            allCBSData={profile.allCBSData}
            badgeVerified={Boolean(loan.badge_verified)}
            availableTab={getLoanProfileTabs(profile.sccbReportData)}
            backupContact={userId}
            notes={isCorporate ? loan.notes : ""}
            platform_notes={isProperty ? "" : loan.platform_notes}
            loan_remark={isProperty ? "" : loan.loan_remark}
            personalOnly={!isCorporate}
            loanDocuments={loanType === "personal" ? loan : null}
            applicationComments={applicationComments}
            propertyApplicationId={isProperty ? loanId : null}
            hideContactInfo={profile.hideContactInfo}
            showConciergeNotice={profile.hideContactInfo}
            bankAnalysisContext={{ loanId, loanType, enabled: isProperty || Boolean(loan.precheck) }}
            mlcbAnalysisContext={{ loanId, enabled: loanType === "personal" && Boolean(loan.allow_lender_visibility && loan.mlcb_report_document) }}
            sccbReportData={profile.sccbReportData}
            sccbReportType="personal"
            sccbExcludeColumns={SCCB_EXCLUDE_COLUMNS}
          />
        ) : <Alert type="info" message="No borrower profile data is available." />}
    </Modal>
  );
}
